import React, { useState, useCallback, useEffect } from 'react';
import { Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { FormDisplayMode, SmartDataTableProps } from '../types';
import { CommonForm } from '../../shared/forms/CommonForm';
import dayjs from 'dayjs';

interface CreateComponentProps {
  api: SmartDataTableProps['api'];
  createForm?: SmartDataTableProps['createForm'];
  formDisplayMode?: FormDisplayMode;
  drawerWidth?: number | string;
  modalWidth?: number | string;
  showCreate?: boolean;
  onSuccess?: (data: any) => void;
  autoOpen?: boolean;
  onDrawerClose?: () => void;
  customButton?: {
    label: string;
    icon?: React.ReactNode;
    style?: React.CSSProperties;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  };
  onResourceChange?: (resource: string) => void;
}

export const CreateComponent: React.FC<CreateComponentProps> = ({
  api,
  createForm,
  formDisplayMode = 'drawer',
  drawerWidth,
  modalWidth,
  showCreate = true,
  onSuccess,
  autoOpen = false,
  onDrawerClose,
  customButton,
  onResourceChange,
}) => {
  const [visible, setVisible] = useState(false);

  // Auto-open the create drawer when autoOpen is true
  useEffect(() => {
    if (autoOpen && createForm) {
      setVisible(true);
    }
  }, [autoOpen, createForm]);

  // Notify parent of resource change
  useEffect(() => {
    if (onResourceChange) {
      onResourceChange(api.resource);
    }
  }, [api.resource, onResourceChange]);

  // Calculate initial values when form opens
  const getInitialValues = useCallback(() => {
    if (!createForm?.fields) return {};

    const initialValues: Record<string, any> = {};

    createForm.fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        // Handle function-based default values
        if (typeof field.defaultValue === 'function') {
          initialValues[field.name] = field.defaultValue();
        } else {
          initialValues[field.name] = field.defaultValue;
        }
      }
    });

    return initialValues;
  }, [createForm?.fields]);

  // Early return if create is disabled
  if (!showCreate) return null;

  // If no createForm is provided, just show the button without form functionality
  if (!createForm) {
    return (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        disabled
        title="Create form not configured"
      >
        Create
      </Button>
    );
  }

  // Always call useForm to avoid conditional hook issues
  const {
    form,
    formProps: createFormProps,
    saveButtonProps: createSaveButtonProps,
  } = useForm({
    resource: api.resource,
    action: 'create',
    redirect: false,
    onMutationSuccess: (data: any) => {
      message.success('Record created successfully!');
      setVisible(false);
      form?.resetFields();
      onSuccess?.(data);
    },
    onMutationError: (error: any) => {
      message.error(
        error?.response?.data?.message ||
          'Failed to create record. Please check your input and try again.'
      );
    },
  });

  // Reset form when drawer visibility changes
  useEffect(() => {
    if (!visible && form) {
      // Reset form when drawer is closed
      form.resetFields();
      form.setFields([]);
    }
  }, [visible, form]);

  // Reset form when initialValues change (for prescription-to-sale flow)
  useEffect(() => {
    if (visible && form && createForm?.initialValues) {
      // Reset form and set new initial values
      form.resetFields();
      form.setFieldsValue(createForm.initialValues);
    }
  }, [visible, form, createForm?.initialValues]);

  // Handle initialValues changes when form is already visible
  useEffect(() => {
    if (
      visible &&
      form &&
      createForm?.initialValues &&
      Object.keys(createForm.initialValues).length > 0
    ) {
      // Update form values without resetting
      form.setFieldsValue(createForm.initialValues);
    }
  }, [createForm?.initialValues, visible, form]);

  const handleClose = () => {
    setVisible(false);
    // Reset form fields
    if (form) {
      form.resetFields();
      // Also clear any validation errors
      form.setFields([]);
    }
    onDrawerClose?.();
  };

  return (
    <>
      <Button
        type={customButton?.type || 'primary'}
        icon={customButton?.icon || <PlusOutlined />}
        onClick={() => setVisible(true)}
        style={customButton?.style}
      >
        {customButton?.label || 'Create'}
      </Button>

      {visible && form && (
        <CommonForm
          key={`create-form-${visible}`}
          fields={createForm.fields}
          title={createForm.title}
          submitText={createForm.submitText}
          initialValues={{ ...createForm.initialValues, ...getInitialValues() }}
          formDisplayMode={formDisplayMode}
          open={visible}
          drawerWidth={drawerWidth}
          modalWidth={modalWidth}
          form={form}
          formProps={createFormProps}
          saveButtonProps={createSaveButtonProps}
          onClose={handleClose}
          onFinish={createFormProps.onFinish}
          resourceName={api.resource}
        />
      )}
    </>
  );
};
